import SEOHead from '@/components/shared/SEOHead';
import SurfaceCard from '@/components/ui/SurfaceCard';

export default function PrivacyPage() {
  return (
    <>
      <SEOHead
        title="Privacy Policy - VideoQR"
        description="VideoQR privacy policy. Learn how we handle your data with a privacy-first approach."
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl font-bold text-white mb-8">Privacy Policy</h1>

        <SurfaceCard className="prose prose-invert max-w-none space-y-6">
          <p className="text-text-secondary">Last updated: March 25, 2026</p>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white">1. Our Privacy Commitment</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              VideoQR is built with a privacy-first approach. We do not track your location, device
              model, or personal browsing behavior. We do not collect data for advertising or sell
              your information to third parties.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white">2. Information We Collect</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              We collect only what is necessary to provide the service:
            </p>
            <ul className="text-text-secondary text-sm leading-relaxed list-disc pl-5 space-y-1">
              <li><strong className="text-white">Email address:</strong> used for account authentication via Firebase Auth. We do not send marketing emails.</li>
              <li><strong className="text-white">Username:</strong> chosen by you, displayed publicly on content you share.</li>
              <li><strong className="text-white">Content you upload:</strong> videos, photos, albums, PDFs, and URLs you choose to create or upload.</li>
              <li><strong className="text-white">Content metadata:</strong> titles, descriptions, and visibility settings you provide.</li>
              <li><strong className="text-white">QR scan counts:</strong> we count the number of times a QR code is scanned. We do not track who scanned it or where.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white">3. What We Do NOT Collect</h2>
            <ul className="text-text-secondary text-sm leading-relaxed list-disc pl-5 space-y-1">
              <li>GPS or location data</li>
              <li>Device model or hardware identifiers</li>
              <li>IP addresses for tracking purposes</li>
              <li>Browsing history or interaction patterns</li>
              <li>Third-party advertising or analytics data</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white">4. Content Authenticity</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              Content captured in the VideoQR iOS app is verified with a SHA-256 cryptographic hash.
              This hash proves the content has not been altered since capture. The hash is a one-way
              fingerprint of the content bytes. It contains no personal information and cannot be
              reversed to reveal anything about the creator.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white">5. Data Storage and Security</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              Your content is stored on Cloudflare R2 with encryption at rest. Account data is stored
              in Cloudflare D1 with encryption at rest. Authentication is handled by Firebase Auth
              (Google Cloud infrastructure). All data is transmitted over HTTPS.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white">6. Third-Party Services</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              We use the following third-party services to operate VideoQR:
            </p>
            <ul className="text-text-secondary text-sm leading-relaxed list-disc pl-5 space-y-1">
              <li><strong className="text-white">Firebase Auth:</strong> account authentication (Google Cloud)</li>
              <li><strong className="text-white">Cloudflare:</strong> content delivery, data storage, and API hosting</li>
            </ul>
            <p className="text-text-secondary text-sm leading-relaxed">
              We do not use any advertising SDKs, analytics trackers, or social media pixels.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white">7. Your Rights</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              You can update your username and profile information at any time. You can change content
              visibility (private, shareable, or public) at any time. You can delete individual content
              or your entire account and all associated data from within the app or web platform.
              Account deletion is immediate and permanent.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white">8. Cookies</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              We use essential cookies and local storage for authentication and session management only.
              We do not use third-party tracking cookies.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white">9. Children's Privacy</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              VideoQR is not intended for children under 13. We do not knowingly collect information
              from children under 13.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white">10. Changes to This Policy</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              We may update this policy from time to time. Changes will be posted on this page with
              an updated date. Continued use of the service constitutes acceptance of any changes.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white">11. Contact</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              For privacy-related questions, contact us at support@video-qr.com.
            </p>
          </section>
        </SurfaceCard>
      </div>
    </>
  );
}
